import React, { useState, useRef, useEffect } from "react";
import {
	format,
	parseISO,
	differenceInMinutes,
	isBefore,
	areIntervalsOverlapping,
} from "date-fns";
import DOMPurify from "dompurify";
import Popover from "./Popover";

const EventWheelItem = ({
	event,
	containerHeight,
	slotHeight,
	scrollOffset,
	isDragging,
	position,
	compact = false,
	onPopoverStateChange,
	onUpdateEvent, // Optional: handle event updates
	onDeleteEvent, // Optional: handle event deletion
}) => {
	const [isPopoverOpen, setIsPopoverOpen] = useState(false);
	const eventRef = useRef(null);
	const [targetRect, setTargetRect] = useState(null);
	const [isMobile, setIsMobile] = useState(false);

	const mouseDownPosRef = useRef(null);

	const handleMouseDown = (e) => {
		mouseDownPosRef.current = { x: e.clientX, y: e.clientY };
	};

	const handleMouseUp = (e) => {
		if (!mouseDownPosRef.current) return;

		const deltaX = Math.abs(e.clientX - mouseDownPosRef.current.x);
		const deltaY = Math.abs(e.clientY - mouseDownPosRef.current.y);

		// Only consider it a click if moved less than 5 pixels in any direction
		if (deltaX < 5 && deltaY < 5) {
			handlePopoverOpen();
		}

		mouseDownPosRef.current = null;
	};

	const formatEventTime = (start, end) => {
		const startMinutes = start.getMinutes();
		const endMinutes = end.getMinutes();
		const startHour = start.getHours();
		const endHour = end.getHours();
		const period = end.getHours() >= 12 ? "pm" : "am";

		let startStr =
			startHour === 0
				? "12"
				: startHour > 12
				? (startHour - 12).toString()
				: startHour.toString();
		if (startMinutes > 0) {
			startStr += `:${startMinutes.toString().padStart(2, "0")}`;
		}

		let endStr =
			endHour === 0
				? "12"
				: endHour > 12
				? (endHour - 12).toString()
				: endHour.toString();
		if (endMinutes > 0) {
			endStr += `:${endMinutes.toString().padStart(2, "0")}`;
		}

		return `${startStr}-${endStr}${period}`;
	};

	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth <= 768);
		};

		checkMobile();
		window.addEventListener("resize", checkMobile);
		return () => window.removeEventListener("resize", checkMobile);
	}, []);

	const startDate =
		event.start instanceof Date ? event.start : parseISO(event.start);
	const endDate = event.end instanceof Date ? event.end : parseISO(event.end);
	const now = new Date();
	const isShortEvent = differenceInMinutes(endDate, startDate) <= 15;

	if (!startDate || !endDate) {
		console.error("Invalid date for event:", event);
		return null;
	}

	const isPast = isBefore(endDate, now);
	const isCurrent = areIntervalsOverlapping(
		{ start: startDate, end: endDate },
		{ start: now, end: now }
	);

	const getEventStyles = () => {
		if (isPast) {
			return "bg-gray-200 hover:bg-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700 group border-2 border-blue-800/20 dark:border-blue-300/20";
		}
		if (isCurrent) {
			return "bg-green-100 hover:bg-green-200 dark:bg-[#132c1a] dark:hover:bg-[#1a3822] group ring-2 ring-green-500/30 border-2 border-blue-800/20 dark:border-blue-300/20";
		}
		return "bg-blue-100 hover:bg-blue-200 dark:bg-[#121f2e] dark:hover:bg-[#172736] group border-2 border-blue-800/20 dark:border-blue-300/20";
	};

	const getDotStyles = () => {
		if (isPast) return "bg-gray-500 dark:bg-gray-400";
		if (isCurrent) return "bg-green-500";
		return "bg-blue-500";
	};

	const getTextStyles = () => {
		if (isPast) {
			return {
				title: "text-gray-700 dark:text-gray-400",
				time: "text-gray-600 dark:text-gray-500",
			};
		}
		if (isCurrent) {
			return {
				title: "text-gray-900 dark:text-gray-100",
				time: "text-gray-700 dark:text-gray-400",
			};
		}
		return {
			title: "text-gray-900 dark:text-gray-100",
			time: "text-gray-700 dark:text-gray-400",
		};
	};

	const getEventHeight = () => {
		const durationMinutes = differenceInMinutes(endDate, startDate);
		return Math.max((durationMinutes / 60) * slotHeight, 24);
	};

	const handlePopoverOpen = () => {
		if (eventRef.current) {
			const rect = eventRef.current.getBoundingClientRect();
			setTargetRect(rect);
			setIsPopoverOpen(true);
			onPopoverStateChange?.(true);
		}
	};

	const handlePopoverClose = () => {
		setIsPopoverOpen(false);
		onPopoverStateChange?.(false);
	};

	const handleEventUpdate = async (updatedEvent) => {
		try {
			await onUpdateEvent?.({
				...event,
				title: updatedEvent.title,
				description: updatedEvent.description,
			});
		} catch (error) {
			console.error("Failed to update event:", error);
		}
	};

	const handleEventDelete = async () => {
		try {
			await onDeleteEvent?.(event.id);
			handlePopoverClose();
		} catch (error) {
			console.error("Failed to delete event:", error);
		}
	};

	const sanitizeAndRenderHtml = (content) => {
		if (!content) return null;

		const containsHtml = /<[a-z][\s\S]*>/i.test(content);

		if (!containsHtml) {
			const contentParts = content.split(/(\bhttps?:\/\/\S+\b)/gi);

			return (
				<div className="space-y-2">
					{contentParts.map((part, i) => {
						if (part.match(/^https?:\/\//i)) {
							return (
								<div key={i} className="break-words">
									<a
										href={part}
										target="_blank"
										rel="noopener noreferrer"
										className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
									>
										{part}
									</a>
								</div>
							);
						}
						return <span key={i}>{part}</span>;
					})}
				</div>
			);
		}

		const sanitizedHtml = DOMPurify.sanitize(content, {
			ALLOWED_TAGS: [
				"b",
				"i",
				"em",
				"strong",
				"a",
				"p",
				"br",
				"ul",
				"ol",
				"li",
				"blockquote",
				"div",
			],
			ALLOWED_ATTR: ["href", "target", "rel", "class"],
		});

		const styledHtml = sanitizedHtml.replace(
			/<a /g,
			'<a class="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 hover:underline break-words" style="word-wrap: break-word;" '
		);

		return (
			<div
				dangerouslySetInnerHTML={{ __html: styledHtml }}
				className="prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
			/>
		);
	};

	const finalPosition = position * slotHeight + scrollOffset;
	const height = getEventHeight();
	const textStyles = getTextStyles();

	if (finalPosition < -height || finalPosition > containerHeight + height) {
		return null;
	}

	return (
		<>
			<div
				ref={eventRef}
				className={`absolute left-12 right-2 rounded-lg transition-colors duration-200 cursor-pointer ${getEventStyles()}`}
				style={{
					top: 0,
					transform: `translateY(${finalPosition}px)`,
					height: `${height}px`,
					minHeight: "24px",
					transition: isDragging ? "none" : "transform 0.1s linear",
					zIndex: 10,
				}}
				onMouseDown={handleMouseDown}
				onMouseUp={handleMouseUp}
			>
				<div className="absolute inset-0 p-1.5 flex flex-col justify-center overflow-hidden">
					{isShortEvent ? (
						<div className="flex items-center space-x-1.5">
							<div
								className={`w-2 h-2 rounded-full flex-shrink-0 ${getDotStyles()}`}
							/>
							<span
								className={`text-xs font-medium truncate flex-1 ${textStyles.title}`}
							>
								{event.title || "Untitled Event"}
							</span>
							<span className={`text-[10px] flex-shrink-0 ${textStyles.time}`}>
								{formatEventTime(startDate, endDate)}
							</span>
						</div>
					) : (
						<>
							<div className="flex items-center space-x-1.5">
								<div
									className={`w-2 h-2 rounded-full flex-shrink-0 ${getDotStyles()}`}
								/>
								<span
									className={`text-xs font-medium truncate ${textStyles.title}`}
								>
									{event.title || "Untitled Event"}
								</span>
							</div>
							<div className={`text-[10px] mt-0.5 ${textStyles.time}`}>
								{formatEventTime(startDate, endDate)}
							</div>
						</>
					)}
				</div>
			</div>

			<Popover
				isOpen={isPopoverOpen}
				onClose={handlePopoverClose}
				targetRect={targetRect}
				event={{
					...event,
					start: startDate,
					end: endDate,
				}}
				onUpdateEvent={handleEventUpdate}
				onDeleteEvent={handleEventDelete}
				canEdit={true}
			>
				<div className="p-3 space-y-4">
					<div>
						<h2
							className={`${
								isMobile ? "text-lg" : "text-xl"
							} font-semibold text-gray-900 dark:text-gray-100 pr-6 mb-1`}
						>
							{event.title}
						</h2>
						<div className="text-sm font-medium text-gray-600 dark:text-gray-300">
							{format(startDate, "MMMM d, yyyy")}
							<br />
							{format(startDate, "h:mm a")} - {format(endDate, "h:mm a")}
						</div>
					</div>

					{event.description && (
						<div className="space-y-2">
							<h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
								Description
							</h3>
							<div className="text-sm text-gray-600 dark:text-gray-300 break-words">
								{sanitizeAndRenderHtml(event.description)}
							</div>
						</div>
					)}

					{event.meetingLink && (
						<div className="pt-2">
							<a
								href={event.meetingLink}
								target="_blank"
								rel="noopener noreferrer"
								className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
							>
								Join Meeting
							</a>
						</div>
					)}
				</div>
			</Popover>
		</>
	);
};

export default EventWheelItem;
